<template>
    <div class="forum-table-page weeduc-admin-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    title="Fórum"
                    subtitle="Gerencie os fóruns do sistema."
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions">
                <v-btn
                    variant="flat"
                    color="primary"
                    rounded="lg"
                    title="Criar novo fórum"
                    :icon="$vuetify.display.xs"
                    class="text-none edit-courses-new-btn"
                    @click="newForumDialog = true"
                >
                    <template v-if="$vuetify.display.xs">
                        <v-icon>mdi-plus</v-icon>
                    </template>
                    <template v-else>
                        <v-icon class="me-1" size="small">mdi-plus</v-icon>
                        Novo fórum
                    </template>
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div class="weeduc-admin-page__panel">
                <div class="weeduc-admin-page__toolbar">
                    <v-row align="end" dense>
                        <v-col cols="12" sm="8" md="6" lg="8">
                            <v-text-field
                                v-model="searchText"
                                label="Buscar"
                                placeholder="Nome do fórum..."
                                class="inputd weeduc-admin-v-field"
                                variant="outlined"
                                density="compact"
                                hide-details
                                clearable
                                append-inner-icon="mdi-magnify"
                                @update:model-value="onSearch"
                            />
                        </v-col>
                        <v-col cols="12" sm="4" md="4" lg="4">
                            <v-select
                                v-model="statusFilter"
                                :items="statusFilterOptions"
                                item-title="text"
                                item-value="value"
                                label="Status"
                                variant="outlined"
                                class="inputd weeduc-admin-v-field"
                                :menu-props="adminSelectMenuProps"
                                @update:modelValue="filtrarForums"
                                density="compact"
                                hide-details
                            />
                        </v-col>
                    </v-row>
                </div>

                <div class="weeduc-admin-page__table-section">
                    <v-table class="custom-table">
                        <thead>
                            <tr>
                                <th class="text-center" style="width: 15%">
                                    ID
                                </th>
                                <th class="forum-table__title-column">
                                    Título
                                </th>
                                <th class="text-center">Status</th>
                                <th class="text-center">Ver</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="forum in paginatedForums"
                                :key="forum.id"
                            >
                                <td class="text-center">#{{ forum.id }}</td>
                                <td class="forum-table__title-column">
                                    {{ forum.title }}
                                </td>
                                <td class="text-center">
                                    <v-chip
                                        density="compact"
                                        :color="getStatusColor(forum.status)"
                                    >
                                        {{ getStatusText(forum.status) }}
                                    </v-chip>
                                </td>
                                <td class="text-center">
                                    <v-btn
                                        color="heading"
                                        @click="editForum(forum.id, forum)"
                                        icon
                                        variant="text"
                                        title="Editar fórum"
                                    >
                                        <Icon name="eye" size="20" />
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </div>

                <v-responsive class="weeduc-admin-page__pagination">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        :total-visible="5"
                        :disabled="!hasReady"
                        @update:model-value="onPageChange"
                    ></v-pagination>
                </v-responsive>

                <Loader v-if="!hasReady" />
            </div>
        </div>

        <!-- Create New Forum Dialog -->
        <v-dialog v-model="newForumDialog" max-width="560">
            <v-card class="modal-card">
                <v-card-title>
                    <h3 class="mb-0">Criar Novo Fórum</h3>
                </v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="newForumTitle"
                        label="Título do Fórum"
                        required
                        variant="outlined"
                        hide-details
                        density="compact"
                        class="mb-4"
                    />

                    <v-textarea
                        v-model="newForumDescription"
                        label="Descrição do Fórum"
                        required
                        auto-grow
                        rows="4"
                        variant="outlined"
                        hide-details
                        density="compact"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="flex-grow-1 cancel-btn"
                        color="grey-darken-4"
                        variant="tonal"
                        @click="closeNewForumDialog"
                    >
                        Cancelar
                    </v-btn>
                    <v-btn
                        class="flex-grow-1 text-white"
                        color="primary"
                        variant="flat"
                        :loading="isCreatingForum"
                        :disabled="!newForumTitle.trim() || isCreatingForum"
                        @click="createNewForum"
                    >
                        Criar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import Loader from '@/components/Loader/loader.vue';
    import TitlePage from '@/components/Weflix/titlePage.vue';
    import ApiForum from '@/utils/forum';
    import { debounce } from 'lodash';
    import { mapState, mapActions } from 'vuex';
    import { toast } from 'vue3-toastify';

    export default {
        components: {
            Loader,
            TitlePage,
        },
        data() {
            return {
                forumsData: [],
                searchText: '',
                currentPage: 1,
                itemsPerPage: 10,
                hasReady: false,
                isCreatingForum: false,
                statusFilter: 'todos',
                statusFilterOptions: [
                    { text: 'Todos', value: 'todos' },
                    { text: 'Aberto', value: 'aberto' },
                    { text: 'Fechado', value: 'fechado' },
                ],
                newForumDialog: false,
                newForumTitle: '',
                newForumDescription: '',
            };
        },
        computed: {
            ...mapState(['token', 'numberIntensTable', 'forum', 'dark']),

            adminSelectMenuProps() {
                return this.dark
                    ? { contentClass: 'weeduc-admin-select-menu-dark' }
                    : {};
            },

            paginatedForums() {
                const startIndex = (this.currentPage - 1) * this.itemsPerPage;
                const endIndex = startIndex + this.itemsPerPage;
                return this.filteredForums.slice(startIndex, endIndex);
            },

            totalPages() {
                return Math.ceil(
                    this.filteredForums.length / this.itemsPerPage
                );
            },

            filteredForums() {
                let filtered = this.forumsData;

                if (this.searchText && this.searchText.trim() !== '') {
                    filtered = filtered.filter((forum) =>
                        forum.title
                            .toLowerCase()
                            .includes(this.searchText.toLowerCase())
                    );
                }

                if (this.statusFilter !== 'todos') {
                    filtered = filtered.filter((forum) => {
                        const isActive = forum.status === 1;
                        return this.statusFilter === 'aberto'
                            ? isActive
                            : !isActive;
                    });
                }

                return filtered;
            },
        },
        created() {
            this.getForumData();
        },
        methods: {
            ...mapActions(['storeForum']),

            getStatusColor(status) {
                return status === 1 ? 'success' : 'error';
            },

            getStatusText(status) {
                return status === 1 ? 'Aberto' : 'Fechado';
            },

            filtrarForums() {
                this.currentPage = 1;
            },

            editForum(id, forum) {
                this.storeForum({ forum: forum });
                this.$router.push('/dashboard/forum/' + id);
            },

            closeNewForumDialog() {
                this.newForumDialog = false;
                this.newForumTitle = '';
                this.newForumDescription = '';
            },

            async getForumData() {
                try {
                    this.hasReady = false;
                    const response = await ApiForum.getDataForum(this.token);
                    this.forumsData = response;
                } catch (err) {
                    console.error('Erro ao carregar dados do Forum');
                    toast.error(
                        'Erro ao carregar dados do Forum. Por favor, tente novamente.'
                    );
                } finally {
                    this.hasReady = true;
                }
            },

            async createNewForum() {
                if (!this.newForumTitle.trim()) {
                    toast.error('Título do fórum é obrigatório');
                    return;
                }

                this.isCreatingForum = true;
                const payload = {
                    title: this.newForumTitle.trim(),
                    description: this.newForumDescription.trim(),
                    status: 1,
                };

                try {
                    const response = await ApiForum.createNewForum(
                        payload,
                        this.token
                    );
                    toast.success('Fórum criado com sucesso!');
                    this.closeNewForumDialog();
                    this.getForumData();
                } catch (err) {
                    toast.error(
                        'Erro ao criar novo fórum. Por favor, tente novamente.'
                    );
                    console.error('Erro ao criar novo fórum:', err);
                } finally {
                    this.isCreatingForum = false;
                }
            },

            onSearch: debounce(function () {
                this.currentPage = 1;
            }, 300),

            onPageChange() {},
        },
    };
</script>

<style scoped>
    @import url('@/css/weeduc/admin/dashboard-weflix-shell.css');
    @import url('@/css/weeduc/admin/forum.css');
    .forum-table__title-column {
        width: 60%;
    }

    .custom-table {
        margin-bottom: 1rem;
    }
</style>
